// src/pages/BookingListPage.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import useDebounce from "../hooks/useDebounce";
import moment from "moment";

import {
  App,
  Card,
  Table,
  Typography,
  Space,
  Tag,
  Button,
  Input,
  DatePicker,
  Select,
  Badge,
  Modal,
  Form,
  Alert,
  Row,
  Col,
  Popconfirm,
  Spin,
  Empty,
  Divider,
  Drawer,
  Descriptions,
  Segmented,
  InputNumber,
  Tooltip,
  Timeline,
  notification as staticNotification,
} from "antd";

import {
  PlusOutlined,
  SyncOutlined,
  EditOutlined,
  DeleteOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  InfoCircleOutlined,
  CreditCardOutlined,
  EyeOutlined,
} from "@ant-design/icons";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";

import "./cs.css";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

/* ───────── Slim table styling ───────── */
const slimTableStyles = `
  .slim-table .ant-table-thead > tr > th {
    padding: 8px 8px !important;
    font-size: 12px;
    font-weight: 600;
    background-color: #fafafa;
    user-select: none;
    cursor: pointer;
    white-space: nowrap;
  }

  .slim-table .ant-table-tbody > tr > td {
    padding: 8px 8px !important;
    font-size: 13px;
  }

  .slim-table .ant-btn-sm {
    height: 24px;
    padding: 0 8px;
    font-size: 12px;
    border-radius: 4px;
  }

  .slim-table .ant-tag {
    margin: 0;
    font-size: 12px;
    padding: 1px 6px;
    border-radius: 4px;
  }

  .slim-table .ant-space {
    gap: 4px !important;
  }

  .slim-table .ant-table-pagination {
    margin: 16px 0 !important;
  }
`;

const STATUS_MAP = {
  0: "Pending",
  1: "Confirmed",
  2: "CheckedIn",
  3: "Cancelled",
  4: "CheckedOut",
};

const normalizeStatus = (raw) => {
  if (raw == null) return "—";
  const s = String(raw).trim();
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return STATUS_MAP[n] ?? s;
  }
  return s;
};

const BookingListPage = () => {
  const appCtx = App.useApp?.();
  const antdNotification = appCtx?.notification;

  const notify = useCallback(
    (type, cfg) => {
      const api = antdNotification;

      const payload = {
        placement: "topRight",
        message: cfg?.title ?? cfg?.message ?? "Notice",
        description: cfg?.description,
        duration: cfg?.duration,
        key: cfg?.key,
      };

      if (api && typeof api[type] === "function") {
        api[type](payload);
        return;
      }

      if (typeof staticNotification[type] === "function") {
        staticNotification[type](payload);
        return;
      }

      // eslint-disable-next-line no-console
      console.warn("Notification API not available:", type, payload);
    },
    [antdNotification]
  );

  const { authData } = useAuth();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  /* Inject styles */
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = slimTableStyles;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  // ─── STATE ───────────────────────────────────────────────
  const [bookings, setBookings] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const [dateRange, setDateRange] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // Info modal
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Drawer
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerBooking, setDrawerBooking] = useState(null);

  // Drawer details fetched by BookingId
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerDetails, setDrawerDetails] = useState(null);
  const [drawerDetailsError, setDrawerDetailsError] = useState(null);

  // Status filter segmented/select
  const [statusFilter, setStatusFilter] = useState("all");

  // Sorting
  const [sortBy, setSortBy] = useState("checkInDate");
  const [descending, setDescending] = useState(false);

  // ─── HELPERS ─────────────────────────────────────────────
  const asNum = (v) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };

  const toNativeDate = (d) => {
    if (!d) return null;
    if (d instanceof Date) return d;
    if (typeof d?.toDate === "function") return d.toDate(); // dayjs/moment
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };

  const toISODate = (d) => {
    const dt = toNativeDate(d);
    if (!dt) return "";
    return dt.toISOString().slice(0, 10);
  };

  const safeDiffNights = (checkIn, checkOut) => {
    const ci = toNativeDate(checkIn);
    const co = toNativeDate(checkOut);
    if (!ci || !co) return 1;
    const ms = co.getTime() - ci.getTime();
    if (!Number.isFinite(ms) || ms <= 0) return 1;
    const nights = Math.ceil(ms / 86400000);
    return Number.isFinite(nights) && nights > 0 ? nights : 1;
  };

 // Replace your getGuestName with this:
const getGuestName = (b) => {
  if (!b) return "";

  // 1) explicit guestName on root
  const direct =
    b.guestName ??
    b.GuestName ??
    `${b.firstName ?? b.FirstName ?? ""} ${b.lastName ?? b.LastName ?? ""}`.trim();

  if (direct) return direct;

  // 2) nested guest object (this is what /Bookings/{id} returns)
  const g = b.guest ?? b.Guest ?? null;
  if (!g) return "";

  return `${g.firstName ?? g.FirstName ?? ""} ${g.lastName ?? g.LastName ?? ""}`.trim();
};

// Add this helper:
const getIdNumber = (b) => {
  if (!b) return "—";
  const g = b.guest ?? b.Guest ?? null;

  return (
    b.idNumber ??
    b.IdNumber ??
    g?.idNumber ??
    g?.IdNumber ??
    "—"
  );
};



  const getBookingId = (b) =>
    b?.bookingId ?? b?.BookingId ?? b?.id ?? b?.bookingNo ?? b?.BookingNo;

  const getBookingNumericId = (b) => {
    const n = Number(b?.bookingId ?? b?.BookingId ?? b?.id);
    return Number.isFinite(n) ? n : null;
  };

  // ✅ Default room resolver (list shows default room)
  const getRoomNumber = (b) => {
    const rn =
      b?.roomNumber ??
      b?.RoomNumber ??
      b?.roomNo ??
      b?.RoomNo ??
      b?.room?.roomNumber ??
      b?.room?.RoomNumber ??
      b?.Room?.roomNumber ??
      b?.Room?.RoomNumber ??
      b?.roomDto?.roomNumber ??
      b?.RoomDto?.RoomNumber;

    if (rn == null || String(rn).trim() === "") {
      const rid = b?.roomId ?? b?.RoomId;
      return rid ? `Room #${rid}` : "—";
    }

    return String(rn);
  };

  const getStatusText = (b) => {
    const raw =
      b?.status ??
      b?.Status ??
      b?.bookingStatus ??
      b?.BookingStatus ??
      b?.paymentStatus ??
      b?.PaymentStatus;
    return normalizeStatus(raw);
  };

  // ── Split-stay / segments (DETAILS) ──────────────────────
  // IMPORTANT FIX:
  // Your /api/Bookings/{id} returns { legs: [...] }.
  // Your drawer was calling extractSegments() which did NOT include legs,
  // therefore it always thought there were no segments.
  const extractSegments = (detailsOrBooking) => {
    const d = detailsOrBooking || {};
    return (
      d?.legs ??
      d?.Legs ??
      d?.roomSegments ??
      d?.RoomSegments ??
      d?.segments ??
      d?.Segments ??
      d?.roomBookings ??
      d?.RoomBookings ??
      d?.rooms ??
      d?.Rooms ??
      []
    );
  };

  const segRoomNumber = (seg) => {
    const rn =
      seg?.roomNumber ??
      seg?.RoomNumber ??
      seg?.room?.roomNumber ??
      seg?.room?.RoomNumber ??
      seg?.Room?.roomNumber ??
      seg?.Room?.RoomNumber;
    if (rn != null && String(rn).trim() !== "") return String(rn);
    const rid = seg?.roomId ?? seg?.RoomId ?? seg?.room?.roomId ?? seg?.Room?.RoomId;
    return rid ? `Room #${rid}` : "—";
  };

  const segRoomTypeName = (seg) => {
    return (
      seg?.roomTypeName ??
      seg?.RoomTypeName ??
      seg?.roomType?.typeName ??
      seg?.RoomType?.TypeName ??
      seg?.room?.roomType?.typeName ??
      seg?.room?.RoomType?.TypeName ??
      seg?.Room?.RoomType?.TypeName ??
      "—"
    );
  };

  const segFromDate = (seg) =>
    seg?.fromDate ??
    seg?.FromDate ??
    seg?.checkInDate ??
    seg?.CheckInDate ??
    seg?.startDate ??
    seg?.StartDate ??
    null;

  const segToDate = (seg) =>
    seg?.toDate ??
    seg?.ToDate ??
    seg?.checkOutDate ??
    seg?.CheckOutDate ??
    seg?.endDate ??
    seg?.EndDate ??
    null;

  const buildRouteString = (segments) => {
    const rooms = (segments || []).map(segRoomNumber).filter((x) => x && x !== "—");
    if (!rooms.length) return "";
    // de-dup consecutive
    const compact = rooms.filter((r, i) => i === 0 || r !== rooms[i - 1]);
    return compact.join(" → ");
  };

  const sortSegments = (segments) => {
    const list = Array.isArray(segments) ? segments : [];
    return [...list].sort((a, b) => {
      const A = toNativeDate(segFromDate(a))?.getTime?.() ?? 0;
      const B = toNativeDate(segFromDate(b))?.getTime?.() ?? 0;
      return A - B;
    });
  };

  // ── Money/price breakdown (unchanged) ─────────────────────
  const discountIsActive = (start, end, checkInDate) => {
    if (!start && !end) return true;
    const d = toNativeDate(checkInDate);
    if (!d) return true;
    const s = start ? toNativeDate(start) : null;
    const e = end ? toNativeDate(end) : null;
    return (!s || d >= s) && (!e || d <= e);
  };

  const formatMoney = (ccy, amount) => {
    const n = asNum(amount);
    const code = (ccy || "BWP").toUpperCase();
    const formatted = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
    return `${code} ${formatted}`;
  };

  const pricePieces = (b) => {
    const inv = b?.invoice || b?.Invoice || b?.invoiceDto || b?.InvoiceDto || {};

    const nightsFromInvoice = asNum(inv.daysBooked ?? inv.DaysBooked);
    const nights = Math.max(
      1,
      nightsFromInvoice > 0
        ? nightsFromInvoice
        : safeDiffNights(b?.checkInDate ?? b?.CheckInDate, b?.checkOutDate ?? b?.CheckOutDate)
    );

    const invTotal = asNum(inv.totalAmount ?? inv.TotalAmount ?? b?.totalAmount ?? b?.TotalAmount);
    const invPaid = asNum(inv.amountPaid ?? inv.AmountPaid ?? b?.paidAmount ?? b?.PaidAmount);

    const invBase = asNum(inv.basePrice ?? inv.BasePrice);
    const invVatPct = asNum(inv.vatPercentage ?? inv.VatPercentage);
    const invLevies = asNum(inv.additionalLevies ?? inv.AdditionalLevies);
    const invCcy = (inv.currency ?? inv.Currency ?? b?.currency ?? b?.Currency ?? "BWP").toUpperCase();

    if (invBase > 0) {
      const vatValue = invBase * (invVatPct / 100);
      const perNight = invBase + vatValue + invLevies;

      const total = invTotal > 0 ? invTotal : perNight * nights;
      const paid = invPaid;
      const due = Math.max(0, total - paid);

      return {
        nights,
        base: invBase,
        baseOriginal: invBase,
        discountPct: 0,
        discountApplied: false,
        vatPct: invVatPct,
        vat: vatValue,
        levies: invLevies,
        perNight,
        total,
        paid,
        due,
        ccy: invCcy,
        source: "invoice",
      };
    }

    const p = b?.priceInfo || b?.PriceInfo || b?.roomPrice || b?.RoomPrice || {};

    const rpBase = asNum(p.basePrice ?? p.BasePrice ?? p.price ?? p.Price ?? p.PricePerNight);
    const rpVatPct = asNum(p.vatPercentage ?? p.VatPercentage);
    const rpLevies = asNum(p.additionalLevies ?? p.AdditionalLevies);

    const rawDisc = asNum(p.discountPct ?? p.DiscountPct ?? p.discount ?? p.Discount);
    const rpStart = p.discountStart ?? p.DiscountStart;
    const rpEnd = p.discountEnd ?? p.DiscountEnd;

    const rpCcy = (p.currency ?? p.Currency ?? invCcy ?? "BWP").toUpperCase();

    if (rpBase > 0) {
      const discOn = discountIsActive(rpStart, rpEnd, b?.checkInDate ?? b?.CheckInDate);
      const isPct = rawDisc >= 0 && rawDisc <= 100;
      const discountPct = discOn && isPct ? rawDisc : 0;

      const baseEffective = rpBase * (1 - discountPct / 100);
      const vatValue = baseEffective * (rpVatPct / 100);
      const perNight = baseEffective + vatValue + rpLevies;

      const total = invTotal > 0 ? invTotal : perNight * nights;
      const paid = invPaid;
      const due = Math.max(0, total - paid);

      return {
        nights,
        base: baseEffective,
        baseOriginal: rpBase,
        discountPct,
        discountApplied: discountPct > 0,
        vatPct: rpVatPct,
        vat: vatValue,
        levies: rpLevies,
        perNight,
        total,
        paid,
        due,
        ccy: rpCcy,
        source: invTotal > 0 ? "roomprice+invoice-total" : "roomprice",
      };
    }

    const totalKnown = asNum(b?.totalAmount ?? b?.TotalAmount ?? invTotal);
    const paid = invPaid;
    const perNight = totalKnown > 0 ? Math.round((totalKnown / nights) * 100) / 100 : 0;
    const due = Math.max(0, totalKnown - paid);

    return {
      nights,
      base: 0,
      baseOriginal: 0,
      discountPct: 0,
      discountApplied: false,
      vatPct: 0,
      vat: 0,
      levies: 0,
      perNight,
      total: totalKnown,
      paid,
      due,
      ccy: invCcy || "BWP",
      source: "derived",
    };
  };

  // ─── FETCH LIST ───────────────────────────────────────────
  const fetchBookings = useCallback(
    async (hotelId) => {
      if (!hotelId) return [];

      setLoading(true);
      try {
        const res = await axiosInstance.get(`/Bookings/all/${hotelId}`, {
          params: {
            sortBy: "checkInDate",
            descending: false,
            from: dateFrom || undefined,
            to: dateTo || undefined,
          },
          validateStatus: (s) => [200, 204, 404].includes(s),
        });

        const list = res.status === 200 ? res.data || [] : [];
        setBookings(list);
        setError(null);
        return list;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error fetching bookings:", err);
        setError("Failed to fetch bookings. Please try again later.");
        notify("error", {
          title: "Error",
          description: "Failed to fetch bookings. Please try again later.",
        });
        return [];
      } finally {
        setLoading(false);
      }
    },
    [dateFrom, dateTo, notify]
  );

  useEffect(() => {
    const hid = authData?.hotelId;
    if (hid) fetchBookings(hid);
    else {
      setError("User authentication is missing. Please log in.");
      navigate("/login");
    }
  }, [authData?.hotelId, fetchBookings, navigate]);

  // ─── FETCH DETAILS ────────────────────────────────────────
  const fetchBookingDetailsById = useCallback(
    async (bookingId) => {
      const id = Number(bookingId);
      if (!Number.isFinite(id) || id <= 0) return null;

      setDrawerLoading(true);
      setDrawerDetailsError(null);

      try {
        const res = await axiosInstance.get(`/Bookings/${id}`, {
          validateStatus: (s) => [200, 404].includes(s),
        });

        if (res.status === 404) {
          setDrawerDetails(null);
          setDrawerDetailsError("Booking details not found.");
          return null;
        }

        const data = res.data ?? null;

        // Normalize legs key if API returns "Legs" vs "legs"
        if (data) {
          const legs = data.legs ?? data.Legs ?? [];
          data.legs = Array.isArray(legs) ? legs : [];

          // normalize rooms meta too (optional)
          if (data.roomsRoute == null && data.RoomsRoute != null) data.roomsRoute = data.RoomsRoute;
          if (data.roomsSummary == null && data.RoomsSummary != null) data.roomsSummary = data.RoomsSummary;
          if (data.roomsCount == null && data.RoomsCount != null) data.roomsCount = data.RoomsCount;
          if (data.isSplitStay == null && data.IsSplitStay != null) data.isSplitStay = data.IsSplitStay;
        }

        setDrawerDetails(data);
        return data;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error fetching booking details:", err);

        setDrawerDetails(null);
        setDrawerDetailsError("Failed to load booking details.");
        notify("error", { title: "Error", description: "Failed to load booking details." });

        return null;
      } finally {
        setDrawerLoading(false);
      }
    },
    [notify]
  );

  const openDrawer = async (b) => {
    setDrawerBooking(b);
    setDrawerDetails(null);
    setDrawerDetailsError(null);
    setShowDrawer(true);

    const id = getBookingNumericId(b);
    if (!id) return;

    await fetchBookingDetailsById(id);
  };

  const closeDrawer = () => {
    setShowDrawer(false);
    setDrawerBooking(null);
    setDrawerDetails(null);
    setDrawerDetailsError(null);
    setDrawerLoading(false);
  };

  // ─── STATUS COUNTS / SEGMENTED ────────────────────────────
  const statusCounts = useMemo(() => {
    const counts = {
      all: bookings.length,
      Confirmed: 0,
      Pending: 0,
      Cancelled: 0,
      CheckedIn: 0,
      CheckedOut: 0,
    };
    bookings.forEach((b) => {
      const s = getStatusText(b);
      if (counts[s] != null) counts[s] += 1;
    });
    return counts;
  }, [bookings]);

  const segmentedOptions = useMemo(
    () => [
      {
        value: "all",
        label: (
          <Space size={6}>
            <Tag>All</Tag>
            <Badge size="small" showZero count={statusCounts.all} />
          </Space>
        ),
      },
      ...["Confirmed", "Pending", "Cancelled", "CheckedIn", "CheckedOut"].map((s) => ({
        value: s,
        label: (
          <Space size={6} key={s}>
            <Tag
              color={
                s === "Confirmed"
                  ? "green"
                  : s === "Pending"
                  ? "orange"
                  : s === "Cancelled"
                  ? "red"
                  : s === "CheckedIn"
                  ? "blue"
                  : "purple"
              }
              style={{ margin: 0 }}
            >
              {s}
            </Tag>
            <Badge size="small" showZero count={statusCounts[s] || 0} />
          </Space>
        ),
      })),
    ],
    [statusCounts]
  );

  // ─── FILTERING ────────────────────────────────────────────
  const filteredBookings = useMemo(() => {
    const q = debouncedSearchQuery.trim().toLowerCase();

    return bookings.filter((b) => {
      const guestName = (getGuestName(b) || "").toLowerCase();
      const roomTypeName = (b?.roomTypeName ?? b?.RoomTypeName ?? "").toLowerCase();
      const roomNo = String(getRoomNumber(b) || "").toLowerCase();
      const idNumber = String(b?.idNumber ?? b?.IdNumber ?? "").toLowerCase();
      const bookingNo = String(b?.bookingNo ?? b?.BookingNo ?? "").toLowerCase();

      const textMatch =
        !q ||
        guestName.includes(q) ||
        roomTypeName.includes(q) ||
        roomNo.includes(q) ||
        idNumber.includes(q) ||
        bookingNo.includes(q);

      const ciISO = toISODate(b?.checkInDate ?? b?.CheckInDate);
      const dateOk =
        (!dateFrom || (ciISO && ciISO >= dateFrom)) &&
        (!dateTo || (ciISO && ciISO <= dateTo));

      const statusOk = statusFilter === "all" ? true : getStatusText(b) === statusFilter;

      return textMatch && dateOk && statusOk;
    });
  }, [bookings, debouncedSearchQuery, dateFrom, dateTo, statusFilter]);

  // ─── SORTING ──────────────────────────────────────────────
  const sortedBookings = useMemo(() => {
    const getSortValue = (b) => {
      if (sortBy === "amount") return pricePieces(b).total;
      if (sortBy === "guestName") return (getGuestName(b) || "").toLowerCase();
      if (sortBy === "roomTypeName") return (b?.roomTypeName ?? b?.RoomTypeName ?? "").toLowerCase();

      if (sortBy === "roomNumber") {
        const rn = getRoomNumber(b);
        const match = String(rn ?? "").match(/\d+/);
        const num = match ? parseInt(match[0], 10) : null;
        return num !== null ? num : String(rn ?? "").toLowerCase();
      }

      if (sortBy === "status") return getStatusText(b);

      if (["checkInDate", "checkOutDate", "bookingDate"].includes(sortBy)) {
        const v = b?.[sortBy] ?? b?.[sortBy[0].toUpperCase() + sortBy.slice(1)];
        const dt = toNativeDate(v) || new Date(0);
        return dt.getTime();
      }

      return b?.[sortBy];
    };

    return [...filteredBookings].sort((a, b) => {
      const A = getSortValue(a);
      const B = getSortValue(b);

      if (A < B) return descending ? 1 : -1;
      if (A > B) return descending ? -1 : 1;
      return 0;
    });
  }, [filteredBookings, sortBy, descending]);

  // ─── PAGINATION ───────────────────────────────────────────
  const displayedBookings = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedBookings.slice(start, start + itemsPerPage);
  }, [sortedBookings, currentPage, itemsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredBookings.length / itemsPerPage));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredBookings.length, itemsPerPage, currentPage]);

  const handleSort = (col) => {
    if (sortBy === col) setDescending((d) => !d);
    else {
      setSortBy(col);
      setDescending(false);
    }
    setCurrentPage(1);
  };

  const handleDateRangeChange = (dates, dateStrings) => {
    setDateRange(dates);
    setDateFrom(dateStrings?.[0] || "");
    setDateTo(dateStrings?.[1] || "");
    setCurrentPage(1);
  };

  // ─── EXPORT HELPERS ───────────────────────────────────────
  const buildExportRows = (rows) =>
    rows.map((b) => {
      const p = pricePieces(b);
      return {
        Guest: getGuestName(b) || "—",
        "ID Number": b?.idNumber ?? b?.IdNumber ?? "—",
        "Room No": getRoomNumber(b) || "—",
        "Room Type": b?.roomTypeName ?? b?.RoomTypeName ?? "—",
        "Check-In": (b?.checkInDate ?? b?.CheckInDate)
          ? new Date(b?.checkInDate ?? b?.CheckInDate).toLocaleDateString()
          : "",
        "Check-Out": (b?.checkOutDate ?? b?.CheckOutDate)
          ? new Date(b?.checkOutDate ?? b?.CheckOutDate).toLocaleDateString()
          : "",
        "Per Night": p.perNight,
        Nights: p.nights,
        Total: p.total,
        Paid: p.paid,
        Due: p.due,
        Status: getStatusText(b),
        "Booking No": b?.bookingNo ?? b?.BookingNo ?? "—",
        "Room ID": b?.roomId ?? b?.RoomId ?? "—",
      };
    });

  const handleExportExcel = () => {
    const rows = buildExportRows(filteredBookings);
    if (!rows.length) {
      notify("warning", {
        title: "No data to export",
        description: "Adjust your filters or date range and try again.",
      });
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bookings");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `BookingReport_${Date.now()}.xlsx`
    );
    notify("success", { title: "Export Successful", description: "Booking report exported to Excel." });
  };

  const handleExportPdf = () => {
    const rows = buildExportRows(filteredBookings);
    if (!rows.length) {
      notify("warning", {
        title: "No data to export",
        description: "Adjust your filters or date range and try again.",
      });
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    doc.text("Booking Report", 14, 10);

    let subtitle = "";
    if (dateFrom || dateTo) subtitle += `Period: ${dateFrom || "..."} - ${dateTo || "..."}`;
    if (statusFilter !== "all") subtitle += subtitle ? ` | Status: ${statusFilter}` : `Status: ${statusFilter}`;

    if (subtitle) {
      doc.setFontSize(10);
      doc.text(subtitle, 14, 16);
      doc.setFontSize(12);
    }

    doc.autoTable({
      startY: subtitle ? 20 : 14,
      head: [[
        "Guest",
        "ID Number",
        "Room No",
        "Room Type",
        "Check-In",
        "Check-Out",
        "Per Night",
        "Nights",
        "Total",
        "Paid",
        "Due",
        "Status",
      ]],
      body: rows.map((r) => [
        r["Guest"],
        r["ID Number"],
        r["Room No"],
        r["Room Type"],
        r["Check-In"],
        r["Check-Out"],
        r["Per Night"],
        r["Nights"],
        r["Total"],
        r["Paid"],
        r["Due"],
        r["Status"],
      ]),
    });

    doc.save(`BookingReport_${Date.now()}.pdf`);
    notify("success", { title: "Export Successful", description: "Booking report exported to PDF." });
  };

  // ─── EDIT / CANCEL / PAYMENT (UNCHANGED) ──────────────────
  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedBooking(null);
    form.resetFields();
  };

  const handleEditClick = (b) => {
    const status = getStatusText(b);
    if (status !== "Confirmed") {
      notify("warning", {
        title: "Cannot Edit",
        description: "This booking is not in a state that allows editing.",
      });
      return;
    }

    const today = new Date();
    const ci = toNativeDate(b?.checkInDate ?? b?.CheckInDate);
    today.setHours(0, 0, 0, 0);

    if (ci) {
      ci.setHours(0, 0, 0, 0);
      if (today > ci) {
        notify("warning", { title: "Cannot Edit", description: "Booking cannot be edited after check-in." });
        return;
      }
    }

    setSelectedBooking(b);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (values) => {
    try {
      const id = selectedBooking?.bookingId ?? selectedBooking?.BookingId;
      if (!id) {
        notify("error", { title: "Error", description: "No booking selected." });
        return;
      }

      const payload = {
        bookingId: id,
        bookingNo: selectedBooking.bookingNo ?? selectedBooking.BookingNo,
        hotelId: selectedBooking.hotelId ?? selectedBooking.HotelId,
        guestId: selectedBooking.guestId ?? selectedBooking.GuestId,
        roomId: selectedBooking.roomId ?? selectedBooking.RoomId,
        roomTypeId: selectedBooking.roomTypeId ?? selectedBooking.RoomTypeId,

        checkInDate: values.checkInDate?.format?.("YYYY-MM-DD"),
        checkOutDate: values.checkOutDate?.format?.("YYYY-MM-DD"),

        totalAmount: Number(selectedBooking.totalAmount ?? selectedBooking.TotalAmount ?? 0),
        paidAmount: Number(selectedBooking.paidAmount ?? selectedBooking.PaidAmount ?? 0),
        discount: Number(selectedBooking.discount ?? selectedBooking.Discount ?? 0),

        paymentStatus: selectedBooking.paymentStatus ?? selectedBooking.PaymentStatus,
        status: selectedBooking.status ?? selectedBooking.Status,

        remarks: (values.remarks ?? "").trim() || "-",
        notes: values.notes ?? selectedBooking.notes ?? selectedBooking.Notes ?? "",

        idNumber: selectedBooking.idNumber ?? selectedBooking.IdNumber,
        firstName: selectedBooking.firstName ?? selectedBooking.FirstName,
        lastName: selectedBooking.lastName ?? selectedBooking.LastName,
        email: selectedBooking.email ?? selectedBooking.Email,
        mobileNumber: selectedBooking.mobileNumber ?? selectedBooking.MobileNumber ?? "",
        phoneNumber: selectedBooking.phoneNumber ?? selectedBooking.PhoneNumber ?? "",
        address: selectedBooking.address ?? selectedBooking.Address ?? "",
        dateOfBirth: selectedBooking.dateOfBirth ?? selectedBooking.DateOfBirth,

        numberOfAdults: selectedBooking.numberOfAdults ?? selectedBooking.NumberOfAdults ?? 1,
        numberOfKids: selectedBooking.numberOfKids ?? selectedBooking.NumberOfKids ?? 0,
        includesKids: selectedBooking.includesKids ?? selectedBooking.IncludesKids ?? false,
      };

      await axiosInstance.put(`/Bookings/${id}`, payload);

      notify("success", { title: "Saved", description: "Booking updated successfully." });

      if (authData?.hotelId) await fetchBookings(authData.hotelId);
      closeEditModal();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error updating booking:", err);
      const api = err?.response?.data;

      if (api?.errors) {
        const flat = Object.entries(api.errors)
          .map(([k, arr]) => `${k}: ${arr.join(", ")}`)
          .join("\n");
        notify("error", { title: api.title || "Validation error", description: flat });
      } else {
        notify("error", { title: "Error", description: api?.message || "Failed to update booking." });
      }
    }
  };

  const handleCancelBooking = async (bookingOrId) => {
    try {
      const id =
        typeof bookingOrId === "object"
          ? bookingOrId.bookingId ?? bookingOrId.BookingId
          : bookingOrId;

      if (!id) {
        notify("error", { title: "Error", description: "No booking selected." });
        return;
      }

      const status = typeof bookingOrId === "object" ? getStatusText(bookingOrId) : null;
      if (status && !["Pending", "Confirmed"].includes(status)) {
        notify("warning", {
          title: "Not Allowed",
          description: `You can only cancel Pending/Confirmed bookings. Current status: ${status}.`,
        });
        return;
      }

      const res = await axiosInstance.post(
        `/Bookings/${id}/cancel`,
        { reason: "Cancelled from booking list" },
        { validateStatus: (s) => s < 500 }
      );

      if (res.status >= 200 && res.status < 300) {
        notify("success", { title: "Cancelled", description: "Booking cancelled successfully." });
        if (authData?.hotelId) await fetchBookings(authData.hotelId);
        closeEditModal();
        return;
      }

      notify("error", {
        title: "Error",
        description: res?.data?.message || "Failed to cancel booking.",
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error cancelling booking:", err);
      notify("error", {
        title: "Error",
        description: err?.response?.data?.message || "Failed to cancel booking.",
      });
    }
  };

  const handleHardDeleteBooking = async (bookingOrId) => {
    try {
      const id =
        typeof bookingOrId === "object"
          ? bookingOrId.bookingId ?? bookingOrId.BookingId
          : bookingOrId;

      if (!id) {
        notify("error", { title: "Error", description: "No booking selected." });
        return;
      }

      const status = typeof bookingOrId === "object" ? getStatusText(bookingOrId) : null;
      if (status && !["Pending", "Cancelled"].includes(status)) {
        notify("warning", {
          title: "Not Allowed",
          description: `Hard delete is only allowed for Pending/Cancelled bookings. Current status: ${status}.`,
        });
        return;
      }

      const res = await axiosInstance.delete(`/Bookings/${id}`, {
        validateStatus: (s) => s < 500,
      });

      if (res.status >= 200 && res.status < 300) {
        notify("success", { title: "Deleted", description: "Booking deleted successfully." });
        if (authData?.hotelId) await fetchBookings(authData.hotelId);
        return;
      }

      notify("error", { title: "Error", description: res?.data?.message || "Failed to delete booking." });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error deleting booking:", err);
      notify("error", {
        title: "Error",
        description: err?.response?.data?.message || "Failed to delete booking.",
      });
    }
  };

  const getStatusTag = (status) => {
    const s = status == null ? "—" : String(status);
    let color = "default";
    if (s === "Confirmed") color = "green";
    else if (s === "Cancelled") color = "red";
    else if (s === "Pending") color = "orange";
    else if (s === "CheckedIn") color = "blue";
    else if (s === "CheckedOut") color = "purple";
    return <Tag color={color}>{s}</Tag>;
  };

  const recordPaymentForBooking = async (booking, detailsValues, amount, method, reference, notes, currency) => {
    const id = booking.bookingId ?? booking.BookingId;
    const hotelId = booking.hotelId ?? booking.HotelId ?? authData?.hotelId;

    try {
      const res = await axiosInstance.post(
        `/Bookings/${id}/payments`,
        {
          hotelId,
          bookingId: id,
          amount: Number(amount),
          currency,
          method,
          reference,
          notes,
        },
        { validateStatus: (s) => [200, 201, 204, 400, 404, 405].includes(s) }
      );
      if ([200, 201, 204].includes(res.status)) return true;
      if (res.status === 400) throw new Error(res.data?.message || "Payment validation failed.");
    } catch {
      // fall back to update booking with bumped paid amount
    }

    const bump = Number(booking.paidAmount ?? booking.PaidAmount ?? 0) + Number(amount);

    const payload = {
      bookingId: id,
      bookingNo: booking.bookingNo ?? booking.BookingNo,
      hotelId,
      guestId: booking.guestId ?? booking.GuestId,
      roomId: booking.roomId ?? booking.RoomId,
      roomTypeId: booking.roomTypeId ?? booking.RoomTypeId,

      checkInDate: detailsValues.checkInDate?.format?.("YYYY-MM-DD"),
      checkOutDate: detailsValues.checkOutDate?.format?.("YYYY-MM-DD"),

      totalAmount: Number(booking.totalAmount ?? booking.TotalAmount ?? 0),
      paidAmount: bump,
      discount: Number(booking.discount ?? booking.Discount ?? 0),

      paymentStatus: booking.paymentStatus ?? booking.PaymentStatus,
      status: booking.status ?? booking.Status,

      remarks: (detailsValues.remarks ?? booking.remarks ?? booking.Remarks ?? "") || "-",
      notes: detailsValues.notes ?? booking.notes ?? booking.Notes ?? "",

      idNumber: booking.idNumber ?? booking.IdNumber,
      firstName: booking.firstName ?? booking.FirstName,
      lastName: booking.lastName ?? booking.LastName,
      email: booking.email ?? booking.Email,
      mobileNumber: booking.mobileNumber ?? booking.MobileNumber ?? "",
      phoneNumber: booking.phoneNumber ?? booking.PhoneNumber ?? "",
      address: booking.address ?? booking.Address ?? "",
      dateOfBirth: booking.dateOfBirth ?? booking.DateOfBirth,

      numberOfAdults: booking.numberOfAdults ?? booking.NumberOfAdults ?? 1,
      numberOfKids: booking.numberOfKids ?? booking.NumberOfKids ?? 0,
      includesKids: booking.includesKids ?? booking.IncludesKids ?? false,
    };

    const res2 = await axiosInstance.put(`/Bookings/${id}`, payload, { validateStatus: (s) => s < 500 });
    if (res2.status >= 200 && res2.status < 300) return true;

    throw new Error(res2.data?.message || "Failed to update booking with payment.");
  };

  // ─── TABLE COLUMNS ────────────────────────────────────────
  const columns = [
    {
      title: "Guest",
      dataIndex: "guestName",
      key: "guestName",
      width: 220,
      render: (_, record) => {
        const name = getGuestName(record) || "Unknown";
        return (
          <Space size={6}>
            <Tag color="blue">#{getBookingId(record) ?? "—"}</Tag>
            <span style={{ display: "inline-block", maxWidth: 140 }} title={name}>
              {name}
            </span>
          </Space>
        );
      },
      onHeaderCell: () => ({ onClick: () => handleSort("guestName") }),
    },
    {
      title: "Room",
      key: "roomNumber",
      width: 140,
      render: (_, record) => {
        const defaultRoom = getRoomNumber(record);

        // Optional hint if your list DTO includes roomsCount etc.
        const hintedCount = Number(record?.roomsCount ?? record?.RoomsCount);
        const splitHint = Number.isFinite(hintedCount) && hintedCount > 1;

        const tip = splitHint
          ? `Default: ${defaultRoom}\nSplit stay: Yes (${hintedCount} rooms)\nOpen View to see move plan.`
          : `Default: ${defaultRoom}`;

        return (
          <Tooltip title={<div style={{ whiteSpace: "pre-wrap" }}>{tip}</div>}>
            <Space size={6}>
              <Tag color={splitHint ? "purple" : "geekblue"} style={{ margin: 0, cursor: "help" }}>
                {defaultRoom}
              </Tag>
              {splitHint && <Badge count={hintedCount} overflowCount={99} />}
            </Space>
          </Tooltip>
        );
      },
      onHeaderCell: () => ({ onClick: () => handleSort("roomNumber") }),
    },
    {
      title: "Room Type",
      dataIndex: "roomTypeName",
      key: "roomTypeName",
      width: 180,
      render: (text, record) => {
        const v = text ?? record?.RoomTypeName ?? "Unknown";
        return (
          <span style={{ display: "inline-block", maxWidth: 170 }} title={v}>
            {v}
          </span>
        );
      },
      onHeaderCell: () => ({ onClick: () => handleSort("roomTypeName") }),
    },
    {
      title: "Check-In",
      dataIndex: "checkInDate",
      key: "checkInDate",
      width: 130,
      render: (text, record) => {
        const v = text ?? record?.CheckInDate;
        return v ? new Date(v).toLocaleDateString() : "—";
      },
      onHeaderCell: () => ({ onClick: () => handleSort("checkInDate") }),
    },
    {
      title: "Check-Out",
      dataIndex: "checkOutDate",
      key: "checkOutDate",
      width: 130,
      render: (text, record) => {
        const v = text ?? record?.CheckOutDate;
        return v ? new Date(v).toLocaleDateString() : "—";
      },
      onHeaderCell: () => ({ onClick: () => handleSort("checkOutDate") }),
    },
    {
      title: "Amount",
      key: "amount",
      width: 180,
      render: (_, record) => {
        const p = pricePieces(record);
        return (
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 13 }}>
              {formatMoney(p.ccy, p.total)}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {formatMoney(p.ccy, p.perNight)}/night
            </Text>
          </Space>
        );
      },
      onHeaderCell: () => ({ onClick: () => handleSort("amount") }),
    },
    {
      title: "Status",
      dataIndex: "paymentStatus",
      key: "status",
      width: 130,
      render: (_, record) => getStatusTag(getStatusText(record)),
      onHeaderCell: () => ({ onClick: () => handleSort("status") }),
    },
    {
      title: "Action",
      key: "actions",
      fixed: "right",
      width: 240,
      render: (_, record) => {
        const status = getStatusText(record);
        const canCancel = ["Pending", "Confirmed"].includes(status);
        const canHardDelete = ["Pending", "Cancelled"].includes(status);

        return (
          <Space size={6} wrap>
            <Button size="small" icon={<EyeOutlined />} onClick={() => openDrawer(record)}>
              View
            </Button>

            <Tooltip title="Edit booking">
              <Button size="small" icon={<EditOutlined />} onClick={() => handleEditClick(record)} />
            </Tooltip>

            <Tooltip title={canCancel ? "Cancel booking (keeps history)" : `Cancel not allowed (status: ${status})`}>
              <Popconfirm
                title="Are you sure you want to cancel this booking?"
                onConfirm={() => handleCancelBooking(record)}
                okText="Yes"
                cancelText="No"
                disabled={!canCancel}
              >
                <Button size="small" icon={<DeleteOutlined />} danger disabled={!canCancel} />
              </Popconfirm>
            </Tooltip>

            <Tooltip title={canHardDelete ? "Hard delete (removes record)" : "Hard delete only Pending/Cancelled"}>
              <Popconfirm
                title="Hard delete removes the booking record. Continue?"
                onConfirm={() => handleHardDeleteBooking(record)}
                okText="Delete"
                cancelText="No"
                disabled={!canHardDelete}
              >
                <Button size="small" danger disabled={!canHardDelete}>
                  Delete
                </Button>
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  // ─── RENDER ───────────────────────────────────────────────
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        title="Bookings"
        extra={
          <Space>
            <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}>
              Excel
            </Button>
            <Button icon={<FilePdfOutlined />} onClick={handleExportPdf}>
              PDF
            </Button>
            <Button icon={<SyncOutlined />} onClick={() => authData?.hotelId && fetchBookings(authData.hotelId)}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/hotels/bookingpage")}>
              New Booking
            </Button>
            <Button icon={<InfoCircleOutlined />} onClick={() => setShowInfoModal(true)}>
              Guide
            </Button>
          </Space>
        }
        variant="outlined"
        styles={{ body: { paddingTop: 8, paddingBottom: 8 } }}
      >
        <div style={{ overflowX: "auto", paddingBottom: 4 }}>
          <Segmented
            options={segmentedOptions}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setCurrentPage(1);
            }}
            size="large"
          />
        </div>
      </Card>

      <Card variant="outlined" styles={{ body: { paddingBottom: 8 } }}>
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" type="primary" onClick={() => authData?.hotelId && fetchBookings(authData.hotelId)}>
                Retry
              </Button>
            }
          />
        )}

        <Space wrap>
          <Input
            style={{ width: 280 }}
            placeholder="Search guest, room, room type, booking no or ID number…"
            allowClear
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onPressEnter={() => setCurrentPage(1)}
          />

          <RangePicker style={{ width: 260 }} onChange={handleDateRangeChange} value={dateRange} />

          <Select
            style={{ width: 220 }}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setCurrentPage(1);
            }}
          >
            <Option value="all">All Status</Option>
            <Option value="Confirmed">Confirmed</Option>
            <Option value="Pending">Pending</Option>
            <Option value="Cancelled">Cancelled</Option>
            <Option value="CheckedIn">Checked In</Option>
            <Option value="CheckedOut">Checked Out</Option>
          </Select>

          <Button type="primary" onClick={() => setCurrentPage(1)}>
            Apply
          </Button>

          <Button
            onClick={() => {
              setSearchQuery("");
              setDateRange([]);
              setDateFrom("");
              setDateTo("");
              setStatusFilter("all");
              setCurrentPage(1);
            }}
          >
            Reset
          </Button>

          <Tag style={{ marginLeft: 6 }}>
            Showing {filteredBookings.length} booking{filteredBookings.length === 1 ? "" : "s"}
          </Tag>
        </Space>
      </Card>

      <Card>
        <Table
          className="slim-table"
          rowKey={(r) =>
            getBookingNumericId(r) ??
            getBookingId(r) ??
            `${r?.roomId ?? "x"}-${r?.checkInDate ?? r?.CheckInDate ?? "0"}`
          }
          loading={loading}
          dataSource={displayedBookings}
          columns={columns}
          tableLayout="fixed"
          scroll={{ x: 1320 }}
          locale={{
            emptyText: loading ? <Spin /> : <Empty description="No bookings found" />,
          }}
          pagination={{
            current: currentPage,
            pageSize: itemsPerPage,
            total: filteredBookings.length,
            onChange: (page) => setCurrentPage(page),
            showTotal: (total) => `${total} booking${total === 1 ? "" : "s"}`,
          }}
        />
      </Card>

      {/* Edit Booking Modal (unchanged from your version) */}
      {showEditModal &&
        selectedBooking &&
        (() => {
          const p = pricePieces(selectedBooking);
          const due = Math.max(0, p?.due ?? 0);
          const ccy = p?.ccy ?? "BWP";

          return (
            <Modal
              title={
                <Space>
                  <EditOutlined />
                  <Text strong>Edit Booking</Text>
                </Space>
              }
              open={showEditModal}
              onCancel={closeEditModal}
              footer={null}
              width={800}
              destroyOnHidden
            >
              <Form
                form={form}
                initialValues={{
                  checkInDate: selectedBooking.checkInDate
                    ? moment(selectedBooking.checkInDate)
                    : selectedBooking.CheckInDate
                    ? moment(selectedBooking.CheckInDate)
                    : null,
                  checkOutDate: selectedBooking.checkOutDate
                    ? moment(selectedBooking.checkOutDate)
                    : selectedBooking.CheckOutDate
                    ? moment(selectedBooking.CheckOutDate)
                    : null,
                  remarks: selectedBooking.remarks ?? selectedBooking.Remarks ?? "-",
                  notes: selectedBooking.notes ?? selectedBooking.Notes ?? "",
                }}
                onFinish={handleSaveEdit}
                layout="vertical"
              >
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="checkInDate"
                      label="Check-In Date"
                      rules={[{ required: true, message: "Please select check-in date" }]}
                    >
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name="checkOutDate"
                      label="Check-Out Date"
                      dependencies={["checkInDate"]}
                      rules={[
                        { required: true, message: "Please select check-out date" },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const ci = getFieldValue("checkInDate");
                            if (!value || !ci || value.isAfter(ci, "day")) return Promise.resolve();
                            return Promise.reject(new Error("Check-out must be after check-in"));
                          },
                        }),
                      ]}
                    >
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="remarks" label="Remarks" rules={[{ required: true, message: "Remarks are required" }]}>
                  <Input.TextArea rows={2} placeholder="Reason / context for the change" />
                </Form.Item>

                <Form.Item name="notes" label="Notes">
                  <Input.TextArea rows={2} placeholder="Internal notes (optional)" />
                </Form.Item>

                <Form.Item>
                  <Space>
                    <Button onClick={closeEditModal}>Cancel</Button>
                    <Button type="primary" htmlType="submit">
                      Save Changes
                    </Button>
                  </Space>
                </Form.Item>
              </Form>

              <Card
                title={
                  <Space>
                    <CreditCardOutlined /> <span>Payment</span>
                  </Space>
                }
                size="small"
                style={{ marginTop: 12 }}
              >
                <Row gutter={12} style={{ marginBottom: 8 }}>
                  <Col span={12}>
                    <Text>Total</Text>
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    <Text strong>{formatMoney(ccy, p?.total ?? 0)}</Text>
                  </Col>

                  <Col span={12}>
                    <Text type="secondary">Paid</Text>
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    <Text type="secondary">{formatMoney(ccy, p?.paid ?? 0)}</Text>
                  </Col>

                  <Col span={12}>
                    <Text type="danger">Due</Text>
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    <Text type="danger" strong>
                      {formatMoney(ccy, due)}
                    </Text>
                  </Col>
                </Row>

                <Form
                  layout="vertical"
                  onFinish={async (vals) => {
                    try {
                      const amt = Number(vals.payAmount || 0);
                      if (!(amt > 0)) {
                        notify("warning", { title: "Warning", description: "Enter a payment amount > 0" });
                        return;
                      }
                      if (amt > due + 0.01) {
                        notify("warning", {
                          title: "Warning",
                          description: `Amount exceeds due (${formatMoney(ccy, due)})`,
                        });
                        return;
                      }

                      const detailsValues = form.getFieldsValue();

                      await recordPaymentForBooking(
                        selectedBooking,
                        detailsValues,
                        amt,
                        vals.payMethod,
                        vals.payRef,
                        vals.payNotes,
                        ccy
                      );

                      notify("success", { title: "Success", description: "Payment recorded" });

                      if (authData?.hotelId) {
                        const list = await fetchBookings(authData.hotelId);
                        const refreshed = list.find((x) => getBookingId(x) === getBookingId(selectedBooking));
                        if (refreshed) setSelectedBooking(refreshed);
                      }
                    } catch (err) {
                      // eslint-disable-next-line no-console
                      console.error(err);
                      notify("error", { title: "Payment failed", description: err?.message || "Please try again." });
                    }
                  }}
                  initialValues={{
                    payAmount: due > 0 ? Number(due.toFixed(2)) : 0,
                    payMethod: "Cash",
                  }}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="payAmount"
                        label={`Amount (${ccy})`}
                        rules={[{ required: true, message: "Enter amount" }]}
                      >
                        <InputNumber min={0.01} precision={2} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item name="payMethod" label="Method" rules={[{ required: true }]}>
                        <Select
                          options={[
                            { value: "Cash", label: "Cash" },
                            { value: "Card", label: "Card" },
                            { value: "BankTransfer", label: "Bank Transfer" },
                            { value: "MobileMoney", label: "Mobile Money" },
                            { value: "Online", label: "Online" },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item name="payRef" label="Reference (optional)">
                    <Input placeholder="Receipt no / POS / Txn id" />
                  </Form.Item>

                  <Form.Item name="payNotes" label="Notes (optional)">
                    <Input.TextArea rows={2} placeholder="Any remarks" />
                  </Form.Item>

                  <Form.Item>
                    <Space>
                      <Button onClick={closeEditModal}>Close</Button>
                      <Button type="primary" htmlType="submit" icon={<CreditCardOutlined />} disabled={due <= 0}>
                        Record Payment
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </Card>
            </Modal>
          );
        })()}

      {/* Info Modal */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined />
            <Text strong>Booking Management Guide</Text>
          </Space>
        }
        open={showInfoModal}
        onCancel={() => setShowInfoModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfoModal(false)}>
            Close
          </Button>,
        ]}
        width={600}
        destroyOnHidden
      >
        <div style={{ padding: "8px 0" }}>
          <Text>This module allows you to manage all bookings for your hotel.</Text>

          <Divider orientation="left" style={{ margin: "16px 0" }}>
            Key Features
          </Divider>
          <ul style={{ paddingLeft: 20 }}>
            <li>View all current and upcoming bookings</li>
            <li>Edit booking dates (before check-in)</li>
            <li>Cancel bookings when needed</li>
            <li>Filter and search bookings easily</li>
            <li>Export booking data to Excel or PDF</li>
          </ul>

          <Divider orientation="left" style={{ margin: "16px 0" }}>
            Current Status
          </Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Text strong>Total Bookings:</Text> {bookings.length}
            </Col>
            <Col span={12}>
              <Text strong>Last Updated:</Text> {new Date().toLocaleString()}
            </Col>
          </Row>
        </div>
      </Modal>

      {/* Booking Details Drawer (UPDATED: shows legs from /Bookings/{id}) */}
      <Drawer
        title={
          <Space>
            <InfoCircleOutlined />
            <Text strong>Booking Details</Text>
          </Space>
        }
        open={showDrawer}
        // AntD v6: Drawer "size" expects "default" | "large" (not a number)
        size="large"
        onClose={closeDrawer}
        destroyOnHidden
      >
        {drawerDetailsError && (
          <Alert
            type="warning"
            showIcon
            message="Details not available"
            description={drawerDetailsError}
            style={{ marginBottom: 12 }}
            action={
              <Button
                size="small"
                onClick={() => {
                  const id = getBookingNumericId(drawerBooking);
                  if (id) fetchBookingDetailsById(id);
                }}
              >
                Retry
              </Button>
            }
          />
        )}

        {/* While details are fetching, show a clear loader instead of "No segments" */}
        {drawerLoading && !drawerDetails ? (
          <div style={{ padding: 18, display: "flex", justifyContent: "center" }}>
            <Spin />
          </div>
        ) : (drawerDetails || drawerBooking) ? (
          (() => {
            const base = drawerDetails || drawerBooking;
            const p = pricePieces(base);
            const statusText = getStatusText(base);

            const segmentsRaw = extractSegments(base);
            const segments = sortSegments(segmentsRaw);

            const apiSplit = Boolean(base?.isSplitStay ?? base?.IsSplitStay);
            const split = apiSplit || segments.length > 1;

            const apiRoute = String(base?.roomsRoute ?? base?.RoomsRoute ?? "").trim();
            const apiSummary = String(base?.roomsSummary ?? base?.RoomsSummary ?? "").trim();
            const route = apiRoute || buildRouteString(segments);

            // Default room can be in API as Room/DefaultRoom. Fallback to list.
            const defaultRoomObj = base?.defaultRoom ?? base?.DefaultRoom ?? base?.room ?? base?.Room ?? null;
            const defaultRoomNumber =
              defaultRoomObj?.roomNumber ??
              defaultRoomObj?.RoomNumber ??
              getRoomNumber(drawerBooking);

            const defaultRoomType =
              defaultRoomObj?.roomType?.typeName ??
              defaultRoomObj?.RoomType?.TypeName ??
              defaultRoomObj?.roomTypeName ??
              defaultRoomObj?.RoomTypeName ??
              base?.roomTypeName ??
              base?.RoomTypeName ??
              "—";

            const segColumns = [
              {
                title: "#",
                key: "seq",
                width: 48,
                render: (_, __, idx) => <Text type="secondary">{idx + 1}</Text>,
              },
              {
                title: "Room",
                key: "room",
                width: 110,
                render: (_, seg) => <Tag color="geekblue">{segRoomNumber(seg)}</Tag>,
              },
              {
                title: "Room Type",
                key: "type",
                width: 170,
                render: (_, seg) => <span title={segRoomTypeName(seg)}>{segRoomTypeName(seg)}</span>,
              },
              {
                title: "From",
                key: "from",
                width: 120,
                render: (_, seg) => {
                  const d = segFromDate(seg);
                  return d ? new Date(d).toLocaleDateString() : "—";
                },
              },
              {
                title: "To",
                key: "to",
                width: 120,
                render: (_, seg) => {
                  const d = segToDate(seg);
                  return d ? new Date(d).toLocaleDateString() : "—";
                },
              },
              {
                title: "Nights",
                key: "nights",
                width: 90,
                render: (_, seg) => safeDiffNights(segFromDate(seg), segToDate(seg)),
              },
              {
                title: "Segment",
                key: "segmentPrice",
                width: 130,
                render: (_, seg) => {
                  const v = seg?.segmentPrice ?? seg?.SegmentPrice;
                  // use booking currency if we have it (invoice.currency usually present)
                  const ccy = (base?.invoice?.currency ?? base?.invoice?.Currency ?? p?.ccy ?? "BWP").toUpperCase();
                  return formatMoney(ccy, v ?? 0);
                },
              },
            ];

            return (
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <Descriptions bordered column={1} size="small" labelStyle={{ width: 180 }}>
                  <Descriptions.Item label="Guest">{getGuestName(base) || "—"}</Descriptions.Item>
                  <Descriptions.Item label="ID Number">{getIdNumber(base)}</Descriptions.Item>


                  <Descriptions.Item label="Default Room">
                    <Space size={8}>
                      <Tag color="blue">{defaultRoomNumber || "—"}</Tag>
                      {split && <Tag color="purple">Split stay</Tag>}
                    </Space>
                  </Descriptions.Item>

                  <Descriptions.Item label="Default Room Type">{defaultRoomType}</Descriptions.Item>

                  <Descriptions.Item label="Check-In">
                    {(base?.checkInDate ?? base?.CheckInDate)
                      ? new Date(base?.checkInDate ?? base?.CheckInDate).toLocaleDateString()
                      : "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Check-Out">
                    {(base?.checkOutDate ?? base?.CheckOutDate)
                      ? new Date(base?.checkOutDate ?? base?.CheckOutDate).toLocaleDateString()
                      : "—"}
                  </Descriptions.Item>

                  <Descriptions.Item label="Booking No">{base?.bookingNo ?? base?.BookingNo ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Status">{getStatusTag(statusText)}</Descriptions.Item>
                </Descriptions>

                {/* Room move plan / legs */}
                <Card
                  title={
                    <Space>
                      <Text strong>Room Move Plan</Text>
                      {split ? (
                        <Tag color="purple">{Number(base?.roomsCount ?? base?.RoomsCount ?? segments.length) || segments.length} rooms</Tag>
                      ) : (
                        <Tag>Single room</Tag>
                      )}
                    </Space>
                  }
                  size="small"
                >
                  {segments.length ? (
                    <>
                      {(apiSummary || route) && (
                        <>
                          {apiSummary && (
                            <Space wrap style={{ marginBottom: 6 }}>
                              <Text type="secondary">Rooms:</Text>
                              <Tag color="purple">{apiSummary}</Tag>
                            </Space>
                          )}
                          {route && (
                            <div style={{ marginBottom: 8 }}>
                              <Text type="secondary">Route:</Text>{" "}
                              <Text strong>{route}</Text>
                            </div>
                          )}
                          <Divider style={{ margin: "10px 0" }} />
                        </>
                      )}

                      <Table
                        size="small"
                        rowKey={(seg, idx) => seg?.roomBookingId ?? seg?.RoomBookingId ?? seg?.id ?? idx}
                        columns={segColumns}
                        dataSource={segments}
                        pagination={false}
                        scroll={{ x: 820 }}
                        locale={{ emptyText: "No room segments" }}
                      />

                      {segments.length > 1 && (
                        <>
                          <Divider style={{ margin: "12px 0" }} />
                          <Text type="secondary">Timeline:</Text>
                          <Timeline
                            style={{ marginTop: 8 }}
                            items={segments.map((seg) => {
                              const from = segFromDate(seg);
                              const to = segToDate(seg);
                              const fromTxt = from ? moment(from).format("YYYY-MM-DD") : "—";
                              const toTxt = to ? moment(to).format("YYYY-MM-DD") : "—";
                              return {
                                children: (
                                  <Space direction="vertical" size={0}>
                                    <Text strong>
                                      {segRoomNumber(seg)} — {segRoomTypeName(seg)}
                                    </Text>
                                    <Text type="secondary">
                                      {fromTxt} to {toTxt}
                                    </Text>
                                  </Space>
                                ),
                              };
                            })}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <Alert
                      type="info"
                      showIcon
                      message="No room segments recorded"
                      description="If this booking involves moving rooms, ensure RoomBookings/legs are saved for this BookingId."
                    />
                  )}
                </Card>

                {/* Price Breakdown (kept) */}
                <Card title="Price Breakdown">
                  <Row gutter={12}>
                    <Col span={12}>
                      <Text>Base / night</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: "right" }}>
                      {p.discountApplied ? (
                        <Space direction="vertical" size={0} style={{ width: "100%", alignItems: "flex-end" }}>
                          <Text type="secondary" delete>
                            {formatMoney(p.ccy, p.baseOriginal)}
                          </Text>
                          <Text strong>{formatMoney(p.ccy, p.base)}</Text>
                          <Tag color="gold">Discount {p.discountPct}%</Tag>
                        </Space>
                      ) : (
                        <Text strong>{formatMoney(p.ccy, p.base)}</Text>
                      )}
                    </Col>

                    <Col span={12}>
                      <Text>VAT ({p.vatPct}%)</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: "right" }}>
                      <Text strong>{formatMoney(p.ccy, p.vat)}</Text>
                    </Col>

                    <Col span={12}>
                      <Text>Fees / night</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: "right" }}>
                      <Text strong>{formatMoney(p.ccy, p.levies)}</Text>
                    </Col>

                    <Col span={24}>
                      <Divider style={{ margin: "8px 0" }} />
                    </Col>

                    <Col span={12}>
                      <Text strong>Total / night</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: "right" }}>
                      <Text strong>{formatMoney(p.ccy, p.perNight)}</Text>
                    </Col>

                    <Col span={12}>
                      <Text>Nights</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: "right" }}>
                      <Text strong>{p.nights}</Text>
                    </Col>

                    <Col span={24}>
                      <Divider style={{ margin: "8px 0" }} />
                    </Col>

                    <Col span={12}>
                      <Text strong>Total</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: "right" }}>
                      <Text strong>{formatMoney(p.ccy, p.total)}</Text>
                    </Col>

                    <Col span={12}>
                      <Text type="secondary">Paid</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: "right" }}>
                      <Text type="secondary">{formatMoney(p.ccy, p.paid)}</Text>
                    </Col>

                    <Col span={12}>
                      <Text type="danger">Due</Text>
                    </Col>
                    <Col span={12} style={{ textAlign: "right" }}>
                      <Text type="danger" strong>
                        {formatMoney(p.ccy, p.due)}
                      </Text>
                    </Col>

                    <Col span={24} style={{ marginTop: 8 }}>
                      <Tag
                        color={
                          p.source === "invoice"
                            ? "purple"
                            : String(p.source || "").startsWith("roomprice")
                            ? "blue"
                            : "default"
                        }
                      >
                        Source: {p.source}
                      </Tag>
                    </Col>
                  </Row>
                </Card>

                {((base?.notes ?? base?.Notes) || (base?.remarks ?? base?.Remarks)) && (
                  <Card title="Notes">
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      {(base?.remarks ?? base?.Remarks) && (
                        <p style={{ marginBottom: 8 }}>
                          <Text strong>Remarks: </Text>
                          {base?.remarks ?? base?.Remarks}
                        </p>
                      )}
                      {(base?.notes ?? base?.Notes) && (
                        <p style={{ marginBottom: 0 }}>
                          <Text strong>Notes: </Text>
                          {base?.notes ?? base?.Notes}
                        </p>
                      )}
                    </div>
                  </Card>
                )}
              </Space>
            );
          })()
        ) : (
          <Empty description="No booking selected" />
        )}
      </Drawer>
    </Space>
  );
};

export default BookingListPage;
